import { useState } from 'react';
import { Icons } from '../Icons';
import { FeatureTooltip } from '../onboarding/FeatureTooltip';

export interface FilterConfig {
  search: string;
  profitLoss: 'all' | 'profit' | 'loss';
  minValue: string;
  maxValue: string;
  dateFrom: string;
  dateTo: string;
  sortBy: 'name' | 'value' | 'pl' | 'plPct' | 'qty';
  sortOrder: 'asc' | 'desc';
}

interface PortfolioFiltersProps {
  filters: FilterConfig;
  onChange: (filters: FilterConfig) => void;
  onReset: () => void;
}

export const DEFAULT_FILTERS: FilterConfig = {
  search: '',
  profitLoss: 'all',
  minValue: '',
  maxValue: '',
  dateFrom: '',
  dateTo: '',
  sortBy: 'value',
  sortOrder: 'desc'
};

/**
 * PortfolioFilters Component
 * Barre de filtres et recherche pour le portfolio
 */
export function PortfolioFilters({ filters, onChange, onReset }: PortfolioFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (key: keyof FilterConfig, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  const hasActiveFilters =
    filters.search !== '' ||
    filters.profitLoss !== 'all' ||
    filters.minValue !== '' ||
    filters.maxValue !== '' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '';

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      {/* Barre de recherche principale */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: showAdvanced ? '16px' : '0' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Icons.Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--muted)'
            }}
          />
          <input
            type="text"
            placeholder="Rechercher un token..."
            value={filters.search}
            onChange={(e) => handleChange('search', e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '40px',
              fontSize: '14px'
            }}
          />
        </div>

        <select
          value={filters.profitLoss}
          onChange={(e) => handleChange('profitLoss', e.target.value)}
          aria-label="Filtrer par profit / loss"
          style={{ minWidth: '140px', fontSize: '14px' }}
        >
          <option value="all">Tous</option>
          <option value="profit">En gain</option>
          <option value="loss">En perte</option>
        </select>

        <FeatureTooltip featureId="portfolio_filters" position="bottom">
          <button
            className="btn-ghost"
            onClick={() => setShowAdvanced(!showAdvanced)}
            title="Filtres avancés"
            style={{ padding: '8px 12px' }}
          >
            <Icons.Filter size={16} />
            {showAdvanced ? <Icons.ChevronUp size={14} /> : <Icons.ChevronDown size={14} />}
          </button>
        </FeatureTooltip>

        {hasActiveFilters && (
          <button
            className="btn-ghost"
            onClick={onReset}
            title="Réinitialiser les filtres"
            style={{ padding: '8px 12px', color: 'var(--danger)' }}
          >
            <Icons.X size={16} />
          </button>
        )}
      </div>

      {/* Filtres avancés (collapsible) */}
      {showAdvanced && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border)'
        }}>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
              Valeur min ($)
            </label>
            <input
              type="number"
              placeholder="0"
              value={filters.minValue}
              onChange={(e) => handleChange('minValue', e.target.value)}
              style={{ width: '100%', fontSize: '14px' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
              Valeur max ($)
            </label>
            <input
              type="number"
              placeholder="∞"
              value={filters.maxValue}
              onChange={(e) => handleChange('maxValue', e.target.value)}
              style={{ width: '100%', fontSize: '14px' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
              Date d'achat (de)
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleChange('dateFrom', e.target.value)}
              style={{ width: '100%', fontSize: '14px' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
              Date d'achat (à)
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleChange('dateTo', e.target.value)}
              style={{ width: '100%', fontSize: '14px' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
              Trier par
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleChange('sortBy', e.target.value)}
              aria-label="Trier par"
              style={{ width: '100%', fontSize: '14px' }}
            >
              <option value="name">Nom</option>
              <option value="value">Valeur</option>
              <option value="pl">P/L $</option>
              <option value="plPct">P/L %</option>
              <option value="qty">Quantité</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
              Ordre
            </label>
            <select
              aria-label="Ordre du tri"
              value={filters.sortOrder}
              onChange={(e) => handleChange('sortOrder', e.target.value as 'asc' | 'desc')}
              style={{ width: '100%', fontSize: '14px' }}
            >
              <option value="desc">Décroissant</option>
              <option value="asc">Croissant</option>
            </select>
          </div>
        </div>
      )}

      {/* Indicateur de filtres actifs */}
      {hasActiveFilters && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid var(--border)',
          fontSize: '12px',
          color: 'var(--muted)'
        }}>
          <Icons.Info size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          Filtres actifs
        </div>
      )}
    </div>
  );
}
